import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'

import styled from 'styled-components'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useClickAway } from 'ahooks'
import i18n from 'live/services/i18n'
import Select from 'live/components/forms/ui/select'
import { StaticDataImporterContext } from '../../context/StaticDataImporterContext'
import { pathHierarchyToEntity } from '../../utils/pathHierarchyToEntity'
import { widthFromString } from '../../utils/widthFromString'
import { TemporaryDataType } from '../useTemporaryDataState'

export const entitiesNames = [
    { from: 'well', to: 'wells' },
    { from: 'intervention', to: 'interventions' },
    { from: 'section', to: 'sections' },
    { from: 'rig table', to: 'rigTables' },
    { from: 'run', to: 'runs' },
    { from: 'cement plug', to: 'cements' },
    { from: 'tubular type', to: 'tubulars' },
    { from: 'restricted pipe movement', to: 'pipeMovements' },
    { from: 'restricted connection', to: 'connections' },
    { from: 'restricted ROP', to: 'rops' }
]

const startFromEntity = {
    wells: 'wells',
    interventions: 'wells',
    sections: 'interventions',
    rigTables: 'sections',
    runs: 'sections',
    cements: 'sections',
    tubulars: 'sections',
    pipeMovements: 'sections',
    connections: 'sections',
    rops: 'sections'
}

export type EntityTypes =
    | WellDto
    | InterventionsDto
    | SectionDto
    | RigTableDto
    | RunDto
    | CementedPlugDto
    | TubularTypeDto
    | TubularRestrictedPipeMovementsDto
    | TubularRestrictedConnectionDto
    | TubularRestrictedRopDto

interface MoveEntityButtonProps {
    entity: EntityTypes
    possibleParents: EntityTypes[]
    parentName: string
    entityName: string
    moveEntityToAnotherParent: (entity: EntityTypes, selectedParent: number) => void
}

export const MoveEntityButton = ({
    entity,
    possibleParents,
    parentName,
    entityName,
    moveEntityToAnotherParent
}: MoveEntityButtonProps): JSX.Element => {
    const [selectedParent, setSelectedParent] = useState<{ value: number; label: string }>(null)
    const [openMoveModal, setOpenMoveModal] = useState(false)

    const { data, unitOptions } = useContext(StaticDataImporterContext)

    const defaultUnit = useMemo(() => unitOptions?.find(u => u?.name === 'in'), [unitOptions])?.name || ''

    const modalRef = useRef<HTMLDivElement>(null)

    useClickAway(() => {
        if (openMoveModal) {
            setOpenMoveModal(false)
            removeHighlightElement()
        }
    }, modalRef)

    const normalizeString = useCallback(
        (parent: EntityTypes): string => {
            const parentRecord = (parent || {}) as Record<string, unknown>
            const parentNameValue = parentRecord?.name as string
            const parentWellId = parentRecord?.wellId as string | number
            const parentRowId = parentRecord?.rowId as string | number
            const parentNumber = parentRecord?.number as string | number
            const parentTubularType = parentRecord?.tubularType as string

            switch (parentName) {
                case 'well':
                    return parentNameValue || `${parentName} ${parentWellId || parentRowId}`
                case 'section':
                    return parentNameValue || `${parentName} ${parentRowId}`
                case 'rig':
                    return parentNameValue || `${parentName} ${parentRowId}`
                case 'run':
                    return parentNameValue || `${parentName} ${parentNumber || parentRowId}`
                case 'intervention':
                    return parentNameValue || `${parentName} ${parentRowId}`
                case 'tubular type':
                    return parentNameValue || `${parentTubularType || parentRowId}`
                default:
                    return parentNameValue || `${parentName} ${parentRowId}`
            }
        },
        [parentName]
    )

    const currentEntityName = entitiesNames?.find(e => e?.from?.toLowerCase() === entityName?.toLowerCase())?.to
    const currentParentNodeId = entity?.parent as number | string | undefined

    const pathHierarchy = useMemo(() => {
        if (entityName === '' || entityName === undefined) {
            return undefined
        }

        return pathHierarchyToEntity({
            data,
            entity,
            startFromEntity: startFromEntity[currentEntityName] as keyof TemporaryDataType,
            omitEntityKeys: [currentEntityName] as (keyof TemporaryDataType)[]
        })
    }, [data, entity, entityName, currentEntityName])

    const parentEntityPaths = useMemo(() => {
        if (entityName === '') {
            return new Map<number, string>()
        }

        const paths = new Map<number, string>()

        possibleParents.forEach(parent => {
            if (parent?.nodeId === currentParentNodeId) {
                return
            }

            const result = pathHierarchyToEntity({
                data,
                entity: parent,
                startFromEntity: startFromEntity[currentEntityName] as keyof TemporaryDataType,
                unit: {
                    sections: defaultUnit
                }
            })

            if (result?.entityPath != null) {
                paths.set(parent.rowId, result.entityPath)
            }
        })

        return paths
    }, [possibleParents, data, currentEntityName, entityName, currentParentNodeId, defaultUnit])

    const parentOptions = useMemo(() => {
        return possibleParents
            .map(parent => {
                if (parent.nodeId === currentParentNodeId) {
                    return null
                }

                const label = entityName === '' ? normalizeString(parent) : parentEntityPaths.get(parent.rowId)

                if (label == null) {
                    return null
                }

                return {
                    value: parent.rowId,
                    label
                }
            })
            .filter(Boolean)
    }, [possibleParents, entityName, normalizeString, parentEntityPaths, currentParentNodeId])

    const handleHighlightParent = (parentId: number): void => {
        const oldSelectedParentElement = document.querySelector(`tr[data-row-key='${selectedParent?.value}']`)
        if (oldSelectedParentElement) {
            oldSelectedParentElement.classList.remove('active-row')
        }

        const newSelectedParentElement = document.querySelector(`tr[data-row-key='${parentId}']`)
        if (newSelectedParentElement) {
            addHighlightElement(newSelectedParentElement)
        }
    }

    const doScrollIntoView = (parentId: number): void => {
        const elem = document.querySelector(`tr[data-row-key='${parentId}']`)
        elem.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    }

    const addHighlightElement = (parentElement: Element): void => {
        parentElement.classList.add('active-row')
    }

    const removeHighlightElement = (): void => {
        const elem = document.querySelector(`tr[data-row-key='${selectedParent?.value}']`)
        if (elem) {
            elem.classList.remove('active-row')
        }
    }

    const componentWidth = widthFromString({
        strA: pathHierarchy?.entityNamePath,
        strB: parentOptions?.map(o => o?.label),
        minWidth: 250
    })

    return (
        <>
            <MoveEntityButtonContainer>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip style={{ zIndex: 9999 }} id="move-entity-tooltip">
                            {`${i18n('Move to another')} ${i18n(parentName)}`}
                        </Tooltip>
                    }
                >
                    <i
                        className="far fa-share"
                        onClick={() => {
                            setOpenMoveModal(prevState => !prevState)
                            if (openMoveModal) {
                                removeHighlightElement()
                            }
                        }}
                    />
                </OverlayTrigger>

                {openMoveModal && (
                    <MoveEntityButtonContent ref={modalRef} width={componentWidth}>
                        <span>
                            {' '}
                            {`${i18n('Select a')} ${i18n(parentName)} ${i18n('you want to move this')} ${i18n(
                                entityName
                            )} ${i18n('to.')}`}
                        </span>
                        <SelectWrapper>
                            <Select
                                style={{ width: componentWidth }}
                                value={selectedParent ?? ''}
                                options={!parentOptions.length ? [] : parentOptions}
                                onChange={(value, option) => {
                                    setSelectedParent(option)
                                    handleHighlightParent(option.value)
                                }}
                                label={pathHierarchy?.entityNamePath}
                                placeholder={i18n('Select...')}
                                // searchable={false}
                                // clearable={false}
                                creatable={false}
                            />
                        </SelectWrapper>
                        <div className="act-btns">
                            <button
                                onClick={() => {
                                    doScrollIntoView(selectedParent.value)
                                    moveEntityToAnotherParent(entity, selectedParent.value)
                                    removeHighlightElement()
                                    setOpenMoveModal(false)
                                    setSelectedParent(null)
                                }}
                                disabled={!selectedParent}
                            >
                                {i18n('Move')}
                            </button>
                            <button
                                onClick={() => {
                                    removeHighlightElement()
                                    setOpenMoveModal(false)
                                    setSelectedParent(null)
                                }}
                                className="cancel-btn"
                            >
                                {i18n('Cancel')}
                            </button>
                        </div>
                    </MoveEntityButtonContent>
                )}
            </MoveEntityButtonContainer>
        </>
    )
}

const MoveEntityButtonContainer = styled.div`
    position: relative;

    .fa-share {
        cursor: pointer;
        color: var(--blue-500);

        &:hover {
            opacity: 0.8;
        }
    }

    .overlay {
        background: rgba(0, 0, 0, 0.5);
        position: fixed;
        width: 100%;
        height: 100%;
        top: 0;
        right: 0;
        z-index: 1;
    }
`

const MoveEntityButtonContent = styled.div<{ width?: string }>`
    position: absolute;
    top: 16px;
    right: 0;
    z-index: 3;
    width: ${props => props?.width || 'fit-content'} !important;

    display: flex;
    flex-direction: column;
    gap: 16px;

    min-width: 250px;
    max-width: none;

    min-height: 180px;

    padding: 16px;

    border-radius: 3px;
    border: 1px solid var(--gray-400);

    box-shadow: rgba(96, 105, 128, 0.4) 0px 2px 14px 0px;
    background-color: var(--gray-0);

    .control-label {
        text-transform: capitalize;
    }

    .act-btns {
        display: flex;
        align-items: end !important;
        gap: 8px;

        button {
            width: fit-content;
            min-width: 75px;
            height: 30px;

            font-size: 12px;
            font-weight: 700;

            border-radius: 3px;
            border: none;

            cursor: pointer;

            color: white;
            background-color: var(--blue-500);

            &:disabled {
                opacity: 0.6;
                cursor: default;
            }

            &:hover:not(:disabled) {
                opacity: 0.8;
            }

            &.cancel-btn {
                border: 1px solid var(--gray-400);
                color: var(--gray-800);
                background-color: transparent;
            }
        }
    }
`

const SelectWrapper = styled.div`
    width: 100%;
    max-height: 50px;
    .live-select.form-group {
        display: flex;
        flex-direction: column;
        align-items: start !important;
    }

    .Select-input {
        overflow: hidden !important;
        padding-left: 5px;
        input {
            padding: 0 !important;
            width: 100% !important;
            height: 30px !important;
        }
    }

    .Select-control {
        justify-content: space-between !important;
    }

    .Select-value {
        padding-right: 35px;
        .Select-value-label {
            overflow: hidden;
            width: 100%;
        }
    }

    .Select-clear-zone {
        margin-left: 30px;
    }

    .Select-menu-outer {
        height: auto !important;
        .Select-menu {
            display: block !important;
            max-height: 140px;
        }

        .Select-option {
            display: block;
            min-height: 30px;
            max-height: 60px;
        }
    }
`
